import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { IconButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/IconButton';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import styles from './ImportMeasurementFiles.module.scss';
import TrashIcon from '@lmt/unicorn-shared/app/icons/trash.svg?react';
import UploadFileIcon from '@lmt/unicorn-shared/app/icons/upload-file.svg?react';
import {
  fileLoaded,
  fileRead,
  pdfFileLoaded,
} from '@lmt/unicorn-shared/app/features/measurements/actions';

import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import {
  readFileAsBlob,
  readFileAsString,
} from '@lmt/unicorn-shared/app/helpers/file-helper';
import { useEventCallback } from '@mui/material';

const MAX_SIZE = 104857600;

export const ImportMeasurementFiles: React.FC<FormComponentProps> = (props) => {
  const { getRootProps, getInputProps, open, acceptedFiles } = useDropzone({
    // Disable click and keydown behavior
    noClick: true,
    noKeyboard: true,
  });
  const { formType, formId, tryCompleteForm } = props;
  const dispatch = useUniDispatch();
  const modals = useModal();
  const i18next = useScopedTranslation('miniscreen.import_form');
  const [sdbdataFile, setSdbdataFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [isSaving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    dispatch(pdfFileLoaded(null));
  }, []);

  useEffect(() => {
    updateDocuments();
  }, [acceptedFiles]);

  const updateDocuments = useEventCallback(() => {
    for (const file of acceptedFiles) {
      if (
        file.path?.toLowerCase().endsWith('.sdbdata') ||
        file.relativePath?.toLowerCase().endsWith('.sdbdata')
      ) {
        setSdbdataFile(file as File);
      } else if (
        file.path?.toLowerCase().endsWith('.pdf') ||
        file.relativePath?.toLowerCase().endsWith('.pdf')
      ) {
        setPdfFile(file as File);
      }
    }
  });

  const onCancel = useEventCallback(async () => {
    await tryCompleteForm(formType, formId, async () => undefined);
  });

  const onSave = useEventCallback(async () => {
    if (!sdbdataFile) {
      return;
    }
    setSaving(true);
    await tryCompleteForm(formType, formId, async () => {
      const sdbdataBlob = await readFileAsBlob(sdbdataFile);
      const sdbdataText = await readFileAsString(sdbdataFile, 'ascii');
      const pdfBlob = pdfFile ? await readFileAsBlob(pdfFile) : null;

      try {
        dispatch(
          fileRead({
            status: 'success',
            data: sdbdataText,
            path: sdbdataFile.path,
          }),
        );
        dispatch(
          fileLoaded({
            fileName: sdbdataFile.path.replace(/^.*[\\\/]/, ''),
            data: sdbdataBlob,
          }),
        );
        dispatch(
          pdfFileLoaded(
            pdfFile
              ? {
                  fileName: pdfFile.path.replace(/^.*[\\\/]/, ''),
                  data: pdfBlob,
                }
              : null,
          ),
        );
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      }
    });
  });

  return (
    <div className={styles.container} data-test-id="importDocumentPopup">
      <div {...(getRootProps({ className: styles.dropzone }) as any)}>
        <input {...(getInputProps() as any)} />
        <UploadFileIcon className={styles.uploadIcon} />
        <div className={styles.text}>{i18next.t('import_text')}</div>
        <Button
          color={ButtonColor.Primary}
          title={i18next.t('upload_button')}
          onClick={open}
          className={styles.uploadButton}
        />
      </div>
      <div className={styles.documentsList}>
        <div className={styles.firstTitle}>
          {i18next.t('measurement')}
          <span className={styles.ext}>
            {i18next.t('measurement_extension')}
          </span>
        </div>
        {sdbdataFile ? (
          <div className={styles.documentItem} data-test-id="sdb-measurement">
            <div className={styles.fileName}>{sdbdataFile.name}</div>
            <div className={styles.options}>
              {sdbdataFile.size > MAX_SIZE && (
                <div className={styles.fileErrorText}>
                  {i18next.t('file_size_limit')}
                </div>
              )}
              <IconButton
                testId={'delete'}
                iconComponent={<TrashIcon className={styles.deleteIcon} />}
                onClick={() => setSdbdataFile(null)}
              />
            </div>
          </div>
        ) : (
          <div className={styles.empty} data-test-id="sdb-measurement">
            {i18next.t('no_data')}
          </div>
        )}
        <div className={styles.title}>
          {i18next.t('report')}
          <span className={styles.ext}>{i18next.t('report_extension')}</span>
        </div>
        {pdfFile ? (
          <div className={styles.documentItem} data-test-id="sdb-report">
            <div className={styles.fileName}>{pdfFile.name}</div>
            <div className={styles.options}>
              {pdfFile.size > MAX_SIZE && (
                <div className={styles.fileErrorText}>
                  {i18next.t('file_size_limit')}
                </div>
              )}
              <IconButton
                testId={'delete'}
                iconComponent={<TrashIcon className={styles.deleteIcon} />}
                onClick={() => setPdfFile(null)}
              />
            </div>
          </div>
        ) : (
          <div className={styles.empty} data-test-id="sdb-report">
            {i18next.t('no_data')}
          </div>
        )}
      </div>
      <div className={styles.buttonContainer} data-test-id={'button-container'}>
        <Button
          className={styles.button}
          onClick={onCancel}
          color={ButtonColor.Primary}
          title={i18next.t('cancel_button')}
        />
        <Button
          disabled={
            !sdbdataFile ||
            isSaving ||
            sdbdataFile.size > MAX_SIZE ||
            (pdfFile?.size ? pdfFile?.size > MAX_SIZE : false)
          }
          title={i18next.t('import_button')}
          className={styles.button}
          onClick={onSave}
        />
      </div>
    </div>
  );
};
